import { normaliserTexte, provinceOfCommune, regionOfCommune } from "@/config/territory";
import { FORMULES, type Compte } from "@/pages/super-admin/data/types";
import type { PlatformAbonnement, PlatformPaiement } from "@/store/billing/types";
import { addMonthsKey, monthLabel, periodRange, toMonthKey } from "@/pages/agence/loyers/periods";

// ---------------------------------------------------------------------------
// Filtres
// ---------------------------------------------------------------------------

export type PeriodeKey = "3m" | "6m" | "12m" | "tout";

export interface StatsFilters {
  periode: PeriodeKey;
  /** "" = toutes les régions. */
  region: string;
  /** "" = toutes les provinces. */
  province: string;
  /** "" = toutes les communes / villes. */
  commune: string;
  /** "" = tous les secteurs / quartiers. */
  secteur: string;
  /** "" = tous les types de compte. */
  type: string;
  /** "" = toutes les formules. */
  formuleId: string;
}

export const DEFAULT_STATS_FILTERS: StatsFilters = {
  periode: "6m",
  region: "",
  province: "",
  commune: "",
  secteur: "",
  type: "",
  formuleId: "",
};

export const PERIODE_OPTIONS: Array<{ value: PeriodeKey; label: string }> = [
  { value: "3m", label: "3 derniers mois" },
  { value: "6m", label: "6 derniers mois" },
  { value: "12m", label: "12 derniers mois" },
  { value: "tout", label: "Tout l'historique" },
];

// ---------------------------------------------------------------------------
// Filtres territoriaux (région / province / commune / secteur).
// Les niveaux absents d'un compte sont déduits de sa ville via @/config/territory,
// ce qui rend l'architecture directement compatible avec de futures colonnes Supabase.
// ---------------------------------------------------------------------------

function matchesTerritoire(compte: Compte, filters: StatsFilters): boolean {
  if (filters.region && regionOfCommune(compte.ville) !== filters.region) return false;
  if (filters.province && provinceOfCommune(compte.ville) !== filters.province) return false;
  if (filters.commune && normaliserTexte(compte.ville) !== normaliserTexte(filters.commune)) return false;
  if (filters.secteur) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Abonnements & contrats
// ---------------------------------------------------------------------------

export type AbonnementCalcule = "actif" | "en_attente" | "expire" | "inactif";

/** Fin de la période d'abonnement mensuelle, calculée depuis la date de création du compte. */
function finAbonnement(compte: Compte): number {
  const date = new Date(compte.createdAt);
  if (Number.isNaN(date.getTime())) return Number.POSITIVE_INFINITY;
  date.setMonth(date.getMonth() + 1);
  return date.getTime();
}

export function statutAbonnement(compte: Compte, now: number): AbonnementCalcule {
  if (finAbonnement(compte) < now) return "expire";
  if (compte.statut === "actif") return "actif";
  if (compte.statut === "en_attente") return "en_attente";
  return "inactif";
}

const CONTRAT_ACTIF_MOTS = ["actif", "cours", "valide", "signe"];

export function isContratActif(statut: string): boolean {
  const value = normaliserTexte(statut);
  return CONTRAT_ACTIF_MOTS.some((mot) => value.includes(mot));
}

export function isPaiementValide(paiement: { statut?: string }): boolean {
  return !paiement.statut || paiement.statut === "valide";
}

// ---------------------------------------------------------------------------
// Périodes (mois)
// ---------------------------------------------------------------------------

function derniersMois(current: string, count: number): string[] {
  const list: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const key = addMonthsKey(current, -i);
    if (key) list.push(key);
  }
  return list;
}

function buildMonths(periode: PeriodeKey, comptes: Compte[]): string[] {
  const current = toMonthKey(new Date());
  if (!current) return [];
  if (periode === "tout") {
    const keys = comptes
      .map((compte) => toMonthKey(new Date(compte.createdAt)))
      .filter(Boolean)
      .sort();
    if (keys.length === 0) return derniersMois(current, 6);
    return periodRange(keys[0], current, 60);
  }
  const count = periode === "12m" ? 12 : periode === "6m" ? 6 : 3;
  return derniersMois(current, count);
}

function moisCourt(label: string): string {
  const [mois, annee] = label.split(" ");
  if (!annee) return label;
  return `${mois.slice(0, 3)} ${annee.slice(2)}`;
}

// ---------------------------------------------------------------------------
// Résultat
// ---------------------------------------------------------------------------

export interface SuperAdminKpis {
  totalUtilisateurs: number;
  proprietaires: number;
  agences: number;
  employes: number;
  immeubles: number;
  biens: number;
  locataires: number;
  contratsActifs: number;
  abonnementsActifs: number;
  abonnementsEnAttente: number;
  abonnementsExpires: number;
  revenusAbonnements: number;
  paiementsEnAttente: number;
  paiementsValides: number;
  comptesActifs: number;
  comptesSuspendus: number;
}

export interface SuperAdminStats {
  kpis: SuperAdminKpis;
  months: Array<{ key: string; label: string }>;
  inscriptions: number[];
  abonnementsActifs: number[];
  abonnementsEnAttente: number[];
  revenus: number[];
  parType: Array<{ key: string; label: string; value: number; token: string }>;
  parFormule: Array<{ key: string; label: string; value: number }>;
  parRegion: Array<{ key: string; label: string; value: number }>;
  total: number;
  hasAnyCompte: boolean;
}

/**
 * Données partagées (mêmes sources que le Dashboard Super Admin).
 * Patrimoine issu du store `platform`, abonnements/paiements issus du store
 * `billing`. Cela garantit qu'une même métrique affiche le même chiffre sur
 * le tableau de bord et sur la page Statistiques.
 */
export interface SuperAdminShared {
  immeubles: number;
  biens: number;
  locataires: number;
  contratsActifs: number;
  abonnements: PlatformAbonnement[];
  paiements: PlatformPaiement[];
}

export function buildSuperAdminStats(
  comptes: Compte[],
  filters: StatsFilters,
  shared: SuperAdminShared
): SuperAdminStats {
  const matchesTypeFormule = (compte: Compte) =>
    (!filters.type || compte.type === filters.type) &&
    (!filters.formuleId || compte.formuleId === filters.formuleId);

  const base = comptes.filter((compte) => matchesTypeFormule(compte) && matchesTerritoire(compte, filters));

  const monthsKeys = buildMonths(filters.periode, base);
  const monthIndex = new Map(monthsKeys.map((key, index) => [key, index]));
  const withinRange = (iso: string) => {
    if (!iso) return false;
    if (filters.periode === "tout") return true;
    const key = toMonthKey(new Date(iso));
    return monthIndex.has(key);
  };

  const filtered = base.filter((compte) => withinRange(compte.createdAt));

  // --- Indicateurs ---
  const kpis: SuperAdminKpis = {
    totalUtilisateurs: filtered.length,
    proprietaires: 0,
    agences: 0,
    employes: 0,
    immeubles: 0,
    biens: 0,
    locataires: 0,
    contratsActifs: 0,
    abonnementsActifs: 0,
    abonnementsEnAttente: 0,
    abonnementsExpires: 0,
    revenusAbonnements: 0,
    paiementsEnAttente: 0,
    paiementsValides: 0,
    comptesActifs: 0,
    comptesSuspendus: 0,
  };

  filtered.forEach((compte) => {
    if (compte.type === "proprietaire") kpis.proprietaires += 1;
    if (compte.type === "agence") kpis.agences += 1;
    if (compte.statut === "actif") kpis.comptesActifs += 1;
    if (compte.statut === "suspendu") kpis.comptesSuspendus += 1;

    kpis.employes += compte.rattachements.employes.length;
  });

  // Patrimoine : même source partagée que le Dashboard Super Admin.
  kpis.immeubles = shared.immeubles;
  kpis.biens = shared.biens;
  kpis.locataires = shared.locataires;
  kpis.contratsActifs = shared.contratsActifs;

  // Abonnements & paiements de plateforme : même source partagée (store billing).
  kpis.abonnementsActifs = shared.abonnements.filter((item) => item.statut === "actif").length;
  kpis.abonnementsEnAttente = shared.abonnements.filter((item) => item.statut === "en_attente").length;
  kpis.abonnementsExpires = shared.abonnements.filter((item) => item.statut === "expire").length;
  kpis.revenusAbonnements = shared.paiements
    .filter((item) => isPaiementValide(item))
    .reduce((sum, item) => sum + item.montant, 0);
  kpis.paiementsValides = shared.paiements.filter((item) => isPaiementValide(item)).length;
  kpis.paiementsEnAttente = shared.paiements.filter((item) => item.statut === "en_attente").length;

  // --- Séries temporelles ---
  const inscriptions = new Array(monthsKeys.length).fill(0);
  const abonnementsActifsSeries = new Array(monthsKeys.length).fill(0);
  const abonnementsEnAttenteSeries = new Array(monthsKeys.length).fill(0);
  const revenusSeries = new Array(monthsKeys.length).fill(0);

  monthsKeys.forEach((key, index) => {
    monthIndex.set(key, index);
  });

  filtered.forEach((compte) => {
    const key = toMonthKey(new Date(compte.createdAt));
    const index = monthIndex.get(key);
    if (index === undefined) return;
    inscriptions[index] += 1;
  });

  // Abonnements et revenus : calculés depuis la même source partagée que les KPI.
  shared.abonnements.forEach((abonnement) => {
    const index = monthIndex.get(toMonthKey(new Date(abonnement.createdAt)));
    if (index === undefined) return;
    if (abonnement.statut === "actif") abonnementsActifsSeries[index] += 1;
    else if (abonnement.statut === "en_attente") abonnementsEnAttenteSeries[index] += 1;
  });

  shared.paiements.forEach((paiement) => {
    if (!isPaiementValide(paiement)) return;
    const index = monthIndex.get(toMonthKey(new Date(paiement.date)));
    if (index === undefined) return;
    revenusSeries[index] += paiement.montant;
  });

  const months = monthsKeys.map((key) => ({ key, label: moisCourt(monthLabel(key)) }));

  // --- Répartitions ---
  const parType = [
    {
      key: "proprietaire",
      label: "Propriétaires",
      value: filtered.filter((compte) => compte.type === "proprietaire").length,
      token: "primary-500",
    },
    {
      key: "agence",
      label: "Agences",
      value: filtered.filter((compte) => compte.type === "agence").length,
      token: "accent-500",
    },
    {
      key: "utilisateur",
      label: "Utilisateurs",
      value: filtered.filter((compte) => compte.type === "utilisateur").length,
      token: "secondary-500",
    },
  ];

  const parFormule = FORMULES.map((formule) => ({
    key: formule.id,
    label: formule.nom,
    value: filtered.filter((compte) => compte.formuleId === formule.id).length,
  }));

  const regionCounts = new Map<string, number>();
  filtered.forEach((compte) => {
    const region = regionOfCommune(compte.ville) || "Région non renseignée";
    regionCounts.set(region, (regionCounts.get(region) ?? 0) + 1);
  });
  const parRegion = Array.from(regionCounts.entries())
    .map(([label, value]) => ({ key: label, label, value }))
    .sort((a, b) => b.value - a.value);

  return {
    kpis,
    months,
    inscriptions,
    abonnementsActifs: abonnementsActifsSeries,
    abonnementsEnAttente: abonnementsEnAttenteSeries,
    revenus: revenusSeries,
    parType,
    parFormule,
    parRegion,
    total: comptes.length,
    hasAnyCompte: comptes.length > 0,
  };
}